import MethodOfContact from 'components/common/MethodOfContact';
import FormTextField from 'components/common/FormTextField';
import Customer from 'models/CustomerModel';
import React, { useState, useEffect, useRef } from 'react';
import { Edit, Save, AlertCircle } from 'lucide-react';
import { updateCustomer } from 'actions/customerActions';
import { connect } from 'react-redux';
import MethodOfContactTypes from 'models/types/MethodOfContactTypes';
import Address from 'models/AddressModel';
import FormField from 'components/common/FormField';

interface Props {
    customer: Customer;
    isEditMode?: boolean;
    handleChange?: (e: any) => void;
    showEditButton?: boolean;
    onEditModeChange?: (isEditing: boolean) => void;
    dispatch?: any;
}

interface ValidationErrors {
    firstName?: string;
    lastName?: string;
    primaryEmail?: string;
}

const CustomerDetailsForm: React.FC<Props> = ({
    customer,
    handleChange: externalHandleChange,
    isEditMode: externalIsEditMode = false,
    showEditButton = false,
    onEditModeChange,
    dispatch,
}: Props) => {
    const [isEditMode, setIsEditMode] = useState(externalIsEditMode);
    const [editedCustomer, setEditedCustomer] = useState<Customer | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>({});
    const [formValid, setFormValid] = useState(true);
    const firstNameValid = useRef(true);
    const lastNameValid = useRef(true);
    const emailValid = useRef(true);

    useEffect(() => {
        setIsEditMode(externalIsEditMode);
    }, [externalIsEditMode]);

    useEffect(() => {
        if (isEditMode) {
            setEditedCustomer({ ...customer });
            // Reset validation state when entering edit mode
            setErrors({});
            setFormValid(true);
            firstNameValid.current = true;
            lastNameValid.current = true;
            emailValid.current = true;
        }
    }, [isEditMode, customer]);

    const handleInternalChange = (event: React.FormEvent<HTMLInputElement>) => {
        const { name, value } = event.currentTarget;

        if (editedCustomer) {
            const updatedCustomer = {
                ...editedCustomer,
                [name]: value,
            };
            setEditedCustomer(updatedCustomer);

            // Clear error when field is changed
            if (errors[name as keyof ValidationErrors]) {
                setErrors({
                    ...errors,
                    [name]: undefined,
                });
            }
        }
    };

    const handleChange = externalHandleChange || handleInternalChange;
    const handleChangeAddress = (event: React.FormEvent<HTMLInputElement>) => {
        const { name, value } = event.currentTarget;

        if (editedCustomer) {
            const updatedCustomer = {
                ...editedCustomer,
                address: {
                    ...editedCustomer.address,
                    [name]: value,
                },
            };
            setEditedCustomer(updatedCustomer);
        }
    };
    const getAddressValue = (propertyName: keyof Address): string => {
        const value = currentCustomer.address ? currentCustomer.address[propertyName] : '';
        return typeof value === 'string' ? value : '';
    };
    const currentCustomer = editedCustomer && isEditMode ? editedCustomer : customer;

    const getStringValue = (propertyName: keyof Customer): string => {
        const value = currentCustomer[propertyName];
        return typeof value === 'string' ? value : '';
    };

    const getMethodOfContact = (): MethodOfContactTypes => {
        return currentCustomer.preferredMethodOfContact || MethodOfContactTypes.Phone;
    };

    const handleMethodOfContactChange = (MOC: MethodOfContactTypes) => {
        if (editedCustomer) {
            const updatedCustomer = {
                ...editedCustomer,
                preferredMethodOfContact: MOC,
            };
            setEditedCustomer(updatedCustomer);
        }
    };

    const handleFirstNameValidation = (isValid: boolean) => {
        firstNameValid.current = isValid;
        updateFormValidity();
    };

    const handleLastNameValidation = (isValid: boolean) => {
        lastNameValid.current = isValid;
        updateFormValidity();
    };

    const handleEmailValidation = (isValid: boolean) => {
        emailValid.current = isValid;
        updateFormValidity();
    };

    const updateFormValidity = () => {
        const isFormValid = firstNameValid.current && lastNameValid.current && emailValid.current;
        setFormValid(isFormValid);
    };

    const validateForm = (): boolean => {
        const newErrors: ValidationErrors = {};
        let valid = true;

        // Validate first name
        if (!currentCustomer.firstName || currentCustomer.firstName.trim() === '') {
            newErrors.firstName = 'First Name is required';
            valid = false;
        }

        // Validate last name
        if (!currentCustomer.lastName || currentCustomer.lastName.trim() === '') {
            newErrors.lastName = 'Last Name is required';
            valid = false;
        }

        // Validate email (optional basic format validation)
        if (currentCustomer.primaryEmail && !validateEmail(currentCustomer.primaryEmail)) {
            newErrors.primaryEmail = 'Please enter a valid email address';
            valid = false;
        }

        setErrors(newErrors);
        setFormValid(valid);
        return valid;
    };

    const validateEmail = (email: string): boolean => {
        // Basic email validation
        return email === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const toggleEditMode = () => {
        // If we're in edit mode and trying to save, validate first
        if (isEditMode) {
            const isValid = validateForm();
            if (!isValid) {
                // Don't exit edit mode if validation fails
                return;
            }
        }

        const newEditMode = !isEditMode;
        setIsEditMode(newEditMode);

        if (onEditModeChange) {
            onEditModeChange(newEditMode);
        }

        if (!newEditMode && editedCustomer && dispatch) {
            // Save changes
            dispatch(updateCustomer(editedCustomer, true));
        }
    };

    return (
        <div className="flex flex-col">
            <div className="grid grid-cols-1 md:grid-cols-2 mb-4 py-3">
                <FormTextField
                    label="First Name"
                    name="firstName"
                    value={getStringValue('firstName')}
                    onChange={handleChange}
                    disabled={!isEditMode}
                    required
                    error={errors.firstName}
                    onValidationChange={handleFirstNameValidation}
                />
                <FormTextField
                    label="Last Name"
                    name="lastName"
                    value={getStringValue('lastName')}
                    onChange={handleChange}
                    disabled={!isEditMode}
                    required
                    error={errors.lastName}
                    onValidationChange={handleLastNameValidation}
                />
                <FormTextField
                    label="Email"
                    name="primaryEmail"
                    value={getStringValue('primaryEmail')}
                    onChange={handleChange}
                    disabled={!isEditMode}
                    type="email"
                    error={errors.primaryEmail}
                    validateField={() =>
                        currentCustomer.primaryEmail && !validateEmail(currentCustomer.primaryEmail)
                            ? 'Please enter a valid email address'
                            : undefined
                    }
                    onValidationChange={handleEmailValidation}
                />

                <div className="w-full my-2 px-4 flex flex-col">
                    <label className="block text-sm font-semibold text-gray-500 mb-1">
                        Preferred method of contact
                    </label>
                    <div className="w-full md:w-1/2">
                        <MethodOfContact
                            selected={getMethodOfContact()}
                            updateMethodOfContact={handleMethodOfContactChange}
                            isDisabled={!isEditMode}
                        />
                    </div>
                </div>
                <FormTextField
                    label="Address"
                    name="address1"
                    value={getAddressValue('address1')}
                    onChange={handleChangeAddress}
                    disabled={!isEditMode}
                />

                <FormTextField
                    label="ZIP Code"
                    name="ZIP"
                    value={getAddressValue('ZIP')}
                    onChange={handleChangeAddress}
                    disabled={!isEditMode}
                />
                {!isEditMode && !customer.notes ? (
                    <div className="w-full my-2 px-4">
                        <label className="block text-sm font-semibold text-gray-500 mb-1">Notes</label>
                        <span className="text-gray-600 italic text-sm">No notes available</span>
                    </div>
                ) : (
                    <FormField
                        label="Notes"
                        name="notes"
                        value={getStringValue('notes')}
                        // @ts-ignore
                        onChange={handleChange}
                        disabled={!isEditMode}
                        showEmptyState={!isEditMode && !currentCustomer.notes}
                        emptyStateText="No notes"
                        type="textarea"
                    />
                )}
            </div>

            {showEditButton && (
                <div className="border-t border-gray-200 px-6 py-3 bg-gray-50 flex justify-end">
                    <button
                        className={`${
                            isEditMode
                                ? formValid
                                    ? 'bg-green-400 hover:bg-green-500'
                                    : 'bg-green-300 cursor-not-allowed'
                                : 'bg-gray-400 hover:bg-gray-500'
                        } rounded-lg px-3 py-2 font-semibold flex items-center text-white transition-colors`}
                        onClick={toggleEditMode}
                        disabled={isEditMode && !formValid}
                    >
                        {isEditMode ? <Save size={16} /> : <Edit size={16} />}
                        <span className="ml-2">{isEditMode ? 'Save' : 'Edit'}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch: any) => ({
    dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerDetailsForm);
